/**
 *
 * When field rearrangement is active, a cell at visual position X may actually be field Y
 * moved from its original position Z. The backend doesn't know about client-side
 * field rearrangement, so updates must be sent to the original address with the original field name.
 *
 * This function takes a cell address (from rearranged data) and finds the original
 * un-rearranged cell Record from raw data.
 *
 * @param cell - The cell address from the UI (rearranged _cell, e.g. "C2")
 * @param settingsData - The rearranged settings OrderedMap (cells have updated _cell)
 * @param rawData - The full raw journal data OrderedMap (original addresses)
 * @returns The original cell Record from rawData, or the raw cell at the given address
 */
import { OrderedMap } from 'immutable';
import { pipe, Option } from 'effect';

interface ImmutableCell {
  _cell: string;
  name: string;
  get(key: string): unknown;
}

type CellMap = OrderedMap<string, ImmutableCell>;

export function resolveOriginalCell(
  cell: string,
  settingsData: CellMap | null | undefined,
  rawData: CellMap,
): ImmutableCell | undefined {
  return pipe(
    // Find the rearranged cell by matching _cell to the visual address
    Option.fromNullable(settingsData ? settingsData.find((c: ImmutableCell) => c._cell === cell) : undefined),
    // Get the field name from the rearranged cell
    Option.flatMap((remappedItem) => Option.fromNullable(remappedItem.name)),
    // Find the original cell in raw data by that field name
    Option.flatMap((fieldName) => Option.fromNullable(rawData.find((c: ImmutableCell) => c.name === fieldName))),
    // Fall back to raw data at the given address
    Option.getOrElse(() => rawData.get(cell) as ImmutableCell | undefined),
  );
}
